// Edge-safe (só string/URL, sem import de Node): importado por utils/legacy-redirects.ts, que roda no middleware.
//
// INC-0332 (cs2588 Mégga Imóveis, meggaimoveis.com.br, 30/10/2026): o site antigo rodava no "Broker Gestor" e, com a
// virada do domínio (29/10), TODAS as URLs indexadas passaram a responder 404 — o Google entende que as páginas sumiram
// e derruba as posições. Formatos do site antigo (Wayback + Google + planilha do cliente):
//   /venda/{slug},{REF}  /temporada/{slug},{REF}  /aluguel/{slug},{REF}     ficha — {REF} = tbl_imovel.Referencia
//   /condominio/ver/{slug}-{cidade},{id}                                      página do condomínio
//   /imoveis-para-{venda|temporada|aluguel}?bairros=N&imovel=T&dormitorios=D   listagem
//   /busca-imoveis-venda?imovel=T                                             listagem por tipo
//   /home /imobiliaria /quem-somos /contato /anuncie /lancamentos …           páginas institucionais
// A ficha é servida por REWRITE para /imovel/{slug}/{REF}: a página (site-props, referenciaDaFichaLegada) acha o imóvel
// pela referência e dá o ÚNICO redirect permanente para a canônica /imoveis/…/imovel/{id} — sem cadeia de saltos.
// Listagens e páginas são 301 diretos para a URL já canônica.
//
// GATE por tenant: só os hosts cadastrados em SITES_BROKER_GESTOR (ou ?codsite= nos hosts de teste) — nos outros
// ~300 sites nada muda (esses paths seguem 404 como sempre). Outro cliente vindo do Broker Gestor = mais uma entrada.

export type AcaoBrokerGestor = { tipo: 'redirect' | 'rewrite'; destino: string; status?: 301 };

export type SiteBrokerGestor = {
  codsite: number;
  /** hosts do tenant, sem "www." e em minúsculas */
  hosts: string[];
  /** finalidade do site antigo → finalidade da busca nova (venda|aluguel|temporada); ausente = venda */
  finalidades: Record<string, string>;
  /** slugs de cidade que o site antigo colava no FIM do slug do condomínio (/condominio/ver/carmel-xangri-la,5) */
  cidades: string[];
  /** grafias erradas de cidade no slug da ficha antiga → slug certo ("xangrila" → "xangri-la") */
  cidadesAlias: Record<string, string>;
  /** id do bairro no site antigo (?bairros=23) → [cidade, bairro] nos slugs da busca nova; '-' = sem filtro */
  bairros: Record<string, [string, string]>;
  /** tipo do site antigo (?imovel=casa-fora-de-condominio) → tipo da busca nova; ausente = o próprio slug */
  tipos: Record<string, string>;
  /** ids de /condominio/ver/{slug},{id} que NÃO existem no site novo → lista de condomínios (planilha do cliente) */
  condominiosSemPar: number[];
  /** path antigo (minúsculas, sem barra final) → destino; tem prioridade sobre as regras genéricas */
  paginas: Record<string, string>;
  /** 1º segmento do path antigo (minúsculas) → destino, para famílias inteiras (/2020/…, /category/…, /busca/…) */
  prefixos: Record<string, string>;
};

const XL = 'xangri-la';
const CC = 'capao-da-canoa';

export const SITES_BROKER_GESTOR: SiteBrokerGestor[] = [
  {
    codsite: 2588,
    hosts: ['meggaimoveis.com.br'],
    // 30/10 (Evandro, aba "correcao-temporada"): o site novo NÃO vai trabalhar com temporada — tudo de temporada
    // (listagem antiga e fichas fora do ar) vai para VENDA; ficha com referência publicada continua indo para a canônica.
    finalidades: { venda: 'venda', temporada: 'venda', aluguel: 'venda', locacao: 'venda' },
    cidades: [XL, CC, 'osorio', 'torres', 'imbe', 'atlantida-sul', 'maquine', 'tramandai', 'arroio-do-sal'],
    cidadesAlias: { xangrila: XL, 'xangri-la-rs': XL, 'capao': CC },
    // ids do filtro "Bairros" do site antigo (checkbox bairro_N) → cidade/bairro como estão em all_bairros.xml
    bairros: {
      '1': [XL, 'arpoador'], '3': [CC, 'arroio-teixeira'], '4': [XL, 'atlantida'], '5': ['osorio', 'atlantida-sul'],
      '6': [XL, 'atlantida-sul'], '7': [CC, CC], '8': [CC, 'capao-novo'], '10': [XL, 'cci'], '11': [CC, 'centro'],
      '12': [XL, 'centro'], '13': [XL, 'coqueiro'], '14': [CC, 'curumim'], '15': [CC, 'girassol'], '16': [XL, 'guara'],
      '17': [CC, '-'], '18': [CC, 'jardim-beira-mar'], '20': ['osorio', 'mariapolis'], '21': ['osorio', 'mariapolis'],
      '22': [XL, 'marina'], '23': [XL, 'maristela'], '24': [CC, 'navegantes'], '25': [XL, 'noiva-do-mar'],
      '27': [XL, 'nova-xangri-la'], '28': [XL, 'osorio'], '29': ['torres', '-'], '30': [XL, 'rainha-do-mar'],
      '31': [XL, 'remanso'], '32': [XL, 'santorini'], '33': ['torres', 'torres'], '34': [CC, 'zona-nova'],
      '36': [XL, '-'], '37': [XL, 'coqueiro'], '39': ['osorio', 'centro'], '41': ['maquine', '-'], '45': [XL, 'atlantida'],
      '46': [XL, 'noiva-do-mar'], '48': ['osorio', 'atlantida-sul'], '50': [CC, '-'], '51': ['atlantida-sul', '-'],
      '52': [CC, '-'], '56': ['osorio', 'atlantida-sul'], '57': [XL, 'rainha-do-mar'], '58': ['atlantida-sul', 'mariapolis'],
      '59': [XL, 'atlantida'], '61': [XL, 'atlantida'], '62': [XL, 'arpoador'], '66': ['imbe', '-'],
      '68': ['imbe', 'santa-terezinha'], '69': [XL, 'centro'],
    },
    tipos: { 'casa-fora-de-condominio': 'casa', casas: 'casa', apartamentos: 'apartamento', terrenos: 'terreno' },
    // planilha do cliente (30/10): 64 condomínios do site antigo sem equivalente no novo → /condominios
    condominiosSemPar: [
      194, 729, 730, 735, 754, 756, 763, 765, 783, 784, 788, 808, 839, 854, 878, 901, 926, 1013, 1019, 1024, 1037, 1039,
      1042, 1045, 1047, 1085, 1087, 1095, 1110, 1112, 1114, 1116, 1129, 1179, 1215, 1298, 1318, 1319, 1339, 1347, 1349,
      1408, 1679, 1868, 1929, 1987, 2113, 2172, 2254, 2382, 2544, 2629, 2834, 3350, 4224, 6577, 6808, 6813, 7090, 7637,
      7638, 7647, 7650, 7657,
    ],
    // destinos conforme a planilha do cliente (url_antiga → url_nova), inclusive os do site anterior ao Broker Gestor
    paginas: {
      '/home': '/',
      '/index.php': '/',
      '/anuncie': '/',
      '/anuncie.php': '/',
      '/contato': '/',
      '/contato.php': '/',
      '/enviar.php': '/',
      '/quero-vender': '/',
      '/imobiliaria': '/empresa',
      '/quem-somos': '/empresa',
      '/sobre': '/empresa',
      '/empresa.php': '/empresa',
      '/equipe.php': '/empresa',
      '/lancamentos': '/condominios',
      '/imoveis_empreendimentos.asp': '/condominios',
      '/imoveis_empreendimentos_foto_01.asp': '/condominios',
      '/imoveis_empreendimentos_foto_02.asp': '/condominios',
      '/imoveis_empreendimentos_foto_03.asp': '/condominios',
      '/favoritos': '/imoveis/venda/-/-/-/-',
      '/comparar': '/imoveis/venda/-/-/-/-',
      '/imoveis': '/imoveis/venda/-/-/-/-',
      '/imoveis.php': '/imoveis/venda/-/-/-/-',
      '/teste-busca': '/imoveis/venda/-/-/-/-',
    },
    prefixos: {
      '2020': '/',
      'category': '/',
      'author': '/',
      'comments': '/',
      'abitar': '/',
      'home': '/',
      'contato': '/',
      'quero-vender': '/',
      'agency': '/empresa',
      'sobre': '/empresa',
      'quem-somos': '/empresa',
      'favoritos': '/imoveis/venda/-/-/-/-',
      'comparar': '/imoveis/venda/-/-/-/-',
      'busca': '/imoveis/venda/-/-/-/-',
      'teste-busca': '/imoveis/venda/-/-/-/-',
    },
  },
];

// Mesma régua de utils/legacy-redirects.ts (removerAcentos + minúsculas + espaços → "-"), copiada para não criar
// import circular entre os dois arquivos.
const COMBINING = new RegExp('[\\u0300-\\u036f]', 'g');
const SPACING = new RegExp('[\\u00B4\\u0060\\u005E\\u007E\\u00A8\\u02C6\\u02CB\\u02CA]', 'g');
const semAcento = (v: string): string => v.normalize('NFD').replace(COMBINING, '').replace(SPACING, '').toLowerCase();
const decodificar = (v: string): string => {
  try {
    return decodeURIComponent(v);
  } catch {
    return v;
  }
};
const slug = (v: string | null | undefined): string =>
  semAcento(decodificar(String(v || '')).trim())
    .replace(/[^a-z0-9-]+/g, '-') // pontuação vira separador ("ed.-arcos" → "ed-arcos", "(norte)" → "norte")
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '') || '-';

const hostSemWww = (valor: string): string =>
  String(valor || '')
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, '')
    .replace(/\.+$/, '')
    .replace(/^www\./, '');

/** Tenant do Broker Gestor desta requisição: pelo host (produção) ou pelo ?codsite= nos hosts locais/de teste. */
export const siteBrokerGestor = (
  requestHost: string,
  params: URLSearchParams,
  hostDeTeste: boolean
): SiteBrokerGestor | null => {
  const host = hostSemWww(requestHost);
  const porHost = host ? SITES_BROKER_GESTOR.find((s) => s.hosts.includes(host)) : undefined;
  if (porHost) return porHost;
  if (!hostDeTeste) return null;
  const codsite = Number(params.get('codsite') || '');
  return (codsite && SITES_BROKER_GESTOR.find((s) => s.codsite === codsite)) || null;
};

const FINALIDADES_ANTIGAS = ['venda', 'temporada', 'aluguel', 'locacao'];
// {slug},{REF}: referência = letras/dígitos (com pelo menos 1 dígito), como REFERENCIA_MOLD em legacy-redirects.ts
const SLUG_VIRGULA_REF = /^([^,]*),([A-Za-z0-9]{1,20})$/;
const UTMS = ['utm_source', 'utm_medium', 'utm_id', 'utm_campaign'];

const modelo = (site: SiteBrokerGestor, finalidade: string | null | undefined): string => {
  const f = slug(finalidade);
  return site.finalidades[f] || 'venda';
};

const comUtms = (path: string, params: URLSearchParams, extras: URLSearchParams = new URLSearchParams()): string => {
  for (const utm of UTMS) {
    const valor = params.get(utm);
    if (valor) extras.set(utm, valor);
  }
  const query = extras.toString();
  return query ? `${path}?${query}` : path;
};

const semSufixoDeCidade = (site: SiteBrokerGestor, valor: string): string => {
  const cidade = [...site.cidades].sort((a, b) => b.length - a.length).find((c) => valor.endsWith(`-${c}`));
  const base = cidade ? valor.slice(0, -(cidade.length + 1)) : valor;
  return base || '-';
};

// ?bairros=23 (ou 23,31) → cidade/bairro da busca nova; ids diferentes de cidade → só o 1º vale (uma cidade por URL).
const cidadeEBairro = (site: SiteBrokerGestor, bairros: string | null): [string, string] => {
  const ids = String(bairros || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
  const achados = ids.map((id) => site.bairros[id]).filter((v): v is [string, string] => !!v);
  if (!achados.length) return ['-', '-'];
  const cidade = achados[0][0];
  const nomes = achados.filter((v) => v[0] === cidade && v[1] !== '-').map((v) => v[1]);
  return [cidade, nomes.length ? Array.from(new Set(nomes)).join('+') : '-'];
};

const tipo = (site: SiteBrokerGestor, imovel: string | null): string => {
  const s = slug(imovel);
  if (s === '-') return '-';
  return site.tipos[s] || s;
};

const listagem = (site: SiteBrokerGestor, finalidade: string, params: URLSearchParams): string => {
  const [cidade, bairro] = cidadeEBairro(site, params.get('bairros') || params.get('bairro'));
  const extras = new URLSearchParams();
  const dormitorios = /^\d+/.exec(String(params.get('dormitorios') || ''))?.[0]; // "4m" = 4+
  if (dormitorios && Number(dormitorios) > 0) extras.set('dormitorios', String(Number(dormitorios)));
  return comUtms(`/imoveis/${modelo(site, finalidade)}/${cidade}/${bairro}/-/${tipo(site, params.get('imovel'))}`, params, extras);
};

/**
 * O que fazer com uma URL do site antigo (Broker Gestor) deste tenant: rewrite (ficha, resolvida pela página),
 * redirect 301 (listagem/condomínio/página) ou null (não é URL do site antigo — segue o fluxo normal).
 */
export const resolverBrokerGestor = (
  rawPathname: string,
  params: URLSearchParams,
  site: SiteBrokerGestor
): AcaoBrokerGestor | null => {
  let pathname = String(rawPathname || '/');
  if (pathname.length > 1) pathname = pathname.replace(/\/+$/, '');
  const lower = decodificar(pathname).toLowerCase();

  const pagina = site.paginas[lower];
  if (pagina) return { tipo: 'redirect', destino: comUtms(pagina, params), status: 301 };

  const segmentos = pathname.split('/').filter(Boolean);
  const primeiro = slug(segmentos[0] || '');

  // /condominio/ver/{slug}-{cidade},{id} → listagem do empreendimento (slug igual ao nome, como em all_empreendimentos.xml);
  // condomínio que não existe no site novo (planilha do cliente) → lista de condomínios
  if (primeiro === 'condominio') {
    const ultimo = decodificar(segmentos[segmentos.length - 1] || '');
    const m = SLUG_VIRGULA_REF.exec(ultimo);
    if (m && site.condominiosSemPar.includes(Number(m[2]))) {
      return { tipo: 'redirect', destino: comUtms('/condominios', params), status: 301 };
    }
    const nome = m ? semSufixoDeCidade(site, slug(m[1])) : '-';
    return { tipo: 'redirect', destino: comUtms(`/imoveis/venda/-/-/${nome}/-`, params), status: 301 };
  }

  // /venda/{slug},{REF} (temporada/aluguel idem) → ficha por referência (a página dá o redirect para a canônica).
  // Slug "{tipo}-{N}-dormitorios-para-{finalidade}-{bairro}-em-{cidade}" → formato Mold /imovel/{fin}/{tipo}/{cidade}/-/{REF}:
  // imóvel que saiu do ar cai na listagem cidade+tipo (/imoveis/venda/xangri-la/-/-/casa), que é o destino da planilha.
  if (FINALIDADES_ANTIGAS.includes(primeiro)) {
    const resto = segmentos.slice(1).map(decodificar);
    const m = resto.length === 1 ? SLUG_VIRGULA_REF.exec(resto[0]) : null;
    if (m && /\d/.test(m[2])) {
      const descritivo = slug(m[1]);
      const partes = /^(.+?)(?:-\d+-(?:dormitorios?|quartos?|suites?))?-para-(?:venda|temporada|aluguel|locacao)(?:-(?:.*-)?em-(.+)|-.*)?$/.exec(descritivo); // cidade = depois do último "-em-"; sem "-em-" (…-para-venda-atlantida-sul) fica só o tipo
      if (partes && partes[1] !== '-') {
        const cidade = partes[2] ? site.cidadesAlias[partes[2]] || partes[2] : '-';
        return { tipo: 'rewrite', destino: `/imovel/${modelo(site, primeiro)}/${tipo(site, partes[1])}/${cidade}/-/${m[2]}` };
      }
      // o 1º segmento de /imovel/{slug}/{REF} não pode ser numérico nem finalidade (seria outro formato legado)
      const slugDaFicha = /^\d+$/.test(descritivo) || FINALIDADES_ANTIGAS.includes(descritivo) ? `imovel-${descritivo}` : descritivo;
      return { tipo: 'rewrite', destino: `/imovel/${slugDaFicha}/${m[2]}` };
    }
    // /venda, /venda/qualquer-coisa → a listagem da finalidade
    return { tipo: 'redirect', destino: listagem(site, primeiro, params), status: 301 };
  }

  // /imoveis/page/2 (paginação do site anterior, WordPress) → listagem geral; /imoveis/{finalidade}/… de hoje passa
  if (primeiro === 'imoveis' && slug(segmentos[1] || '') === 'page') {
    return { tipo: 'redirect', destino: comUtms('/imoveis/venda/-/-/-/-', params), status: 301 };
  }

  // famílias inteiras do site anterior (/2020/…, /category/…, /agency/…, /busca/?…, /sobre/embed/)
  const prefixo = site.prefixos[primeiro];
  if (prefixo) return { tipo: 'redirect', destino: comUtms(prefixo, params), status: 301 };

  // /imoveis-para-venda?bairros=23&imovel=casa&dormitorios=3 · /busca-imoveis-venda?imovel=terreno
  const busca = /^(?:imoveis-para|busca-imoveis)-([a-z]+)$/.exec(primeiro);
  if (busca && segmentos.length === 1) {
    return { tipo: 'redirect', destino: listagem(site, busca[1], params), status: 301 };
  }

  return null;
};
